// ── the private ring · what a grant can be narrowed to ───────────────────────
//
// A person's memory of themselves sits in two dozen personal dimensions
// (@innernet/core PERSONAL_DIMENSIONS). Asking someone to clear "affinities"
// for an app, or "temporal_patterns", asks them to think in our filing system.
// So a grant names a CATEGORY — a few words a person would use — and each
// category is a fixed set of those dimensions:
//
//   work     how you work         behavior · tools · habits · use_times · temporal_patterns · rituals
//   taste    what you like        preferences · hobbies · interests · media · influences · inspirations · affinities · aversions
//   voice    how you talk         voice · lexicon · languages
//   mind     what is on your mind state · open_threads · hauntings          (sensitive)
//   people   the people in your life   relationships · kin                  (sensitive)
//   body     your body and health somatic                                   (sensitive)
//
// A sensitive category is one whose facts could hurt if they travelled too far
// (health, inner life, other people). A grant that reaches one defaults to a
// day, never to always (lib/self/ring GRANT_DEFAULT): the person can still
// choose longer, it is only never the default.
//
// A dimension in no category (a new one, a typo) belongs to none: a grant
// narrowed to categories never clears it, so a fact cannot slip through a
// category nobody named. Pure and client-safe; the migration (149) validates
// the same six keys.

export type Category = 'work' | 'taste' | 'voice' | 'mind' | 'people' | 'body'

export interface CategoryDef {
  key: Category
  /** how the person says it: "how you work" */
  label: string
  /** the personal dimensions it gathers */
  dims: readonly string[]
  sensitive: boolean
}

export const CATEGORY_DEFS: readonly CategoryDef[] = [
  { key: 'work', label: 'how you work', dims: ['behavior', 'tools', 'habits', 'use_times', 'temporal_patterns', 'rituals'], sensitive: false },
  {
    key: 'taste',
    label: 'what you like',
    dims: ['preferences', 'hobbies', 'interests', 'media', 'influences', 'inspirations', 'affinities', 'aversions'],
    sensitive: false,
  },
  { key: 'voice', label: 'how you talk', dims: ['voice', 'lexicon', 'languages'], sensitive: false },
  { key: 'mind', label: 'what is on your mind', dims: ['state', 'open_threads', 'hauntings'], sensitive: true },
  { key: 'people', label: 'the people in your life', dims: ['relationships', 'kin'], sensitive: true },
  { key: 'body', label: 'your body and health', dims: ['somatic'], sensitive: true },
]

export const CATEGORIES: readonly Category[] = CATEGORY_DEFS.map((c) => c.key)

const BY_KEY = new Map<Category, CategoryDef>(CATEGORY_DEFS.map((c) => [c.key, c]))
const BY_DIM = new Map<string, Category>(CATEGORY_DEFS.flatMap((c) => c.dims.map((d) => [d, c.key] as const)))

export const isCategory = (v: unknown): v is Category => typeof v === 'string' && BY_KEY.has(v as Category)

/** The category a personal dimension belongs to (null: none). */
export function categoryOf(dimension: string | null | undefined): Category | null {
  if (typeof dimension !== 'string') return null
  return BY_DIM.get(dimension.trim().toLowerCase()) ?? null
}

/** The dimensions a set of categories gathers, in the list's order. */
export function dimsOf(categories: readonly Category[]): string[] {
  return CATEGORY_DEFS.filter((c) => categories.includes(c.key)).flatMap((c) => [...c.dims])
}

/**
 * A list of categories as stored or sent: known keys only, each once, in the
 * list's order. Anything that is not a list — or a list that names none — is
 * null, which means "not narrowed" ONLY where the caller says so; a grant that
 * was meant to be narrowed and names nothing known is refused by the caller.
 */
export function normCategories(v: unknown): Category[] | null {
  if (!Array.isArray(v)) return null
  const keep = CATEGORIES.filter((c) => v.includes(c))
  return keep.length ? keep : null
}

/** Whether any of these categories is a sensitive one. */
export function anySensitive(categories: readonly Category[] | null | undefined): boolean {
  return !!categories && categories.some((c) => BY_KEY.get(c)?.sensitive)
}

/** "how you work and what you like" · "your body and health" */
export function categoryWords(categories: readonly Category[] | null | undefined): string {
  const labels = CATEGORY_DEFS.filter((c) => categories?.includes(c.key)).map((c) => c.label)
  if (!labels.length) return ''
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/** The categories a set of dimensions falls in (unknown dimensions are skipped). */
export function categoriesOfDims(dimensions: Iterable<string | null | undefined>): Category[] {
  const seen = new Set<Category>()
  for (const d of dimensions) {
    const c = categoryOf(d)
    if (c) seen.add(c)
  }
  return CATEGORIES.filter((c) => seen.has(c))
}
